import type { ECharts, EChartsOption } from 'echarts';

type EChartsModule = typeof import('echarts');

export type ChartType = 'line' | 'bar' | 'pie' | 'gauge';

export interface ChartConfig {
  id: string;
  type: ChartType;
  title: string;
  width?: number;
  height?: number;
}

export class ChartService {
  private instances: Map<string, ECharts> = new Map();
  private echartsPromise: Promise<EChartsModule> | null = null;
  private resizeIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();

  private async getECharts(): Promise<EChartsModule> {
    if (!this.echartsPromise) {
      this.echartsPromise = import('echarts');
    }
    return this.echartsPromise;
  }

  async create(
    container: HTMLElement | null,
    config: ChartConfig,
  ): Promise<ECharts | null> {
    if (!container) return null;

    if (this.isHidden(container)) {
      await this.waitForVisible(container);
    }

    await this.waitForContainerSize(container);

    const echarts = await this.getECharts();

    const instance = echarts.init(container, undefined, {
      renderer: 'canvas',
      width: config.width ?? container.clientWidth,
      height: config.height ?? container.clientHeight,
    });

    this.instances.set(config.id, instance);

    window.addEventListener('resize', () => {
      instance.resize();
    });

    this.setupResizeFallback(container, config.id);

    return instance;
  }

  private isHidden(container: HTMLElement): boolean {
    const style = getComputedStyle(container);
    return style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0';
  }

  private waitForVisible(container: HTMLElement, maxAttempts = 100): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        if (!this.isHidden(container)) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          requestAnimationFrame(check);
        } else {
          resolve();
        }
      };
      check();
    });
  }

  private waitForContainerSize(container: HTMLElement, maxAttempts = 200): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        if (container.clientWidth > 0 && container.clientHeight > 0) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          requestAnimationFrame(check);
        } else {
          resolve();
        }
      };
      check();
    });
  }

  private setupResizeFallback(container: HTMLElement, chartId: string): void {
    let lastWidth = container.clientWidth;
    let lastHeight = container.clientHeight;

    const checkResize = () => {
      const currentWidth = container.clientWidth;
      const currentHeight = container.clientHeight;

      if (currentWidth !== lastWidth || currentHeight !== lastHeight) {
        lastWidth = currentWidth;
        lastHeight = currentHeight;
        const instance = this.instances.get(chartId);
        if (instance) {
          try {
            instance.resize();
          } catch {
            // ignore
          }
        }
      }
    };

    const intervalId = setInterval(checkResize, 500);
    this.resizeIntervals.set(chartId, intervalId);
  }

  async update(id: string, option: EChartsOption): Promise<void> {
    const instance = this.instances.get(id);
    if (instance) {
      instance.setOption(option, { notMerge: true });
    }
  }

  dispose(id: string): void {
    const instance = this.instances.get(id);
    if (instance) {
      instance.dispose();
      this.instances.delete(id);
    }
    const intervalId = this.resizeIntervals.get(id);
    if (intervalId) {
      clearInterval(intervalId);
      this.resizeIntervals.delete(id);
    }
  }

  getInstance(id: string): ECharts | undefined {
    return this.instances.get(id);
  }

  disposeAll(): void {
    this.instances.forEach((instance) => instance.dispose());
    this.instances.clear();
    this.resizeIntervals.forEach((intervalId) => clearInterval(intervalId));
    this.resizeIntervals.clear();
  }
}

export function buildLineOption(
  title: string,
  xData: string[],
  series: Array<{ name: string; data: number[]; color?: string }>,
  showSymbol = false,
): EChartsOption {
  return {
    backgroundColor: 'transparent',
    title: {
      text: title,
      textStyle: { color: '#f3f6fa', fontSize: 13 },
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(8, 12, 20, 0.9)',
      borderColor: 'rgba(0, 217, 255, 0.3)',
      textStyle: { color: '#f3f6fa' },
    },
    legend: {
      data: series.map((s) => s.name),
      textStyle: { color: '#8f9cae' },
      bottom: 0,
    },
    grid: {
      top: 40,
      bottom: 40,
      left: 50,
      right: 20,
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLine: { lineStyle: { color: '#1a2332' } },
      axisLabel: { color: '#5c6b80', fontSize: 10 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } },
      axisLabel: { color: '#5c6b80', fontSize: 10 },
    },
    series: series.map((s) => ({
      name: s.name,
      type: 'line',
      smooth: true,
      symbol: showSymbol ? 'circle' : 'none',
      symbolSize: showSymbol ? 6 : 0,
      lineStyle: { width: 2, color: s.color ?? '#00d9ff' },
      data: s.data,
    })),
  };
}

export function buildPieOption(
  title: string,
  data: Array<{ name: string; value: number; itemStyle?: { color: string } }>,
): EChartsOption {
  return {
    backgroundColor: 'transparent',
    title: {
      text: title,
      textStyle: { color: '#f3f6fa', fontSize: 13 },
      left: 'center',
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(8, 12, 20, 0.9)',
      borderColor: 'rgba(0, 217, 255, 0.3)',
      textStyle: { color: '#f3f6fa' },
    },
    series: [
      {
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['50%', '55%'],
        itemStyle: {
          borderRadius: 6,
          borderColor: '#080c14',
          borderWidth: 2,
        },
        label: { color: '#8f9cae', fontSize: 11 },
        data,
      },
    ],
  };
}

export function buildGaugeOption(
  title: string,
  value: number,
  min: number,
  max: number,
  unit: string,
  color?: string,
): EChartsOption {
  return {
    backgroundColor: 'transparent',
    series: [
      {
        type: 'gauge',
        startAngle: 220,
        endAngle: -40,
        min,
        max,
        progress: { show: true, width: 12, itemStyle: { color } },
        axisLine: {
          lineStyle: { width: 12, color: [[1, '#1a2332']] },
        },
        axisTick: { show: false },
        splitLine: { length: 8, lineStyle: { color: '#5c6b80' } },
        axisLabel: { color: '#5c6b80', fontSize: 9 },
        pointer: { show: false },
        title: {
          offsetCenter: [0, '60%'],
          fontSize: 11,
          color: '#8f9cae',
        },
        detail: {
          valueAnimation: true,
          formatter: `{value} ${unit}`,
          color: '#f3f6fa',
          fontSize: 20,
        },
        data: [{ value, name: title }],
      },
    ],
  };
}
